import HomeUi from '../../components/user-interfaces/home/home.ui';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { AuthContextType, useAuth } from '../../hooks/use-auth.hook';
import { useSnackbars } from '../../hooks/snackbar.hook';
import { PageContentDto } from '../../services/dtos/page-content.dto';
import { getPageContent, toPageContentDTO } from '../../services/page.service';
import { hasContract } from '../../services/contracts.service';
import { showAxiosMessageError } from '../../services/error-message.service';
import { Cookies } from 'react-cookie-consent';
import { useConfirm } from 'material-ui-confirm';
import { useLocalStorage } from '../../hooks/use-local-storage.hook';
import DialogCustom from '../../components/others/dialog-custom/dialog-custom';
import parse from 'html-react-parser';
import { AuthDto } from '../../services/dtos/auth.dto';
import { useNavigate } from 'react-router-dom';

/**
 * Composant de la page d'accueil
 *
 * Les données de l'utilisateur sont récupérés ici pour adapter la page si l'utilisateur est connecté ou non.
 *
 * @constructor
 */
export default function HomePage() {
    //Permet de savoir si on est passé une fois dans le useEffect
    const dataFetchedRef = useRef(false);
    //Déclaration de l'alerte d'erreur
    const showMessage = useSnackbars();
    // Etat par défaut du contenu de la page avant récupération dans le back
    let auth: AuthContextType = useAuth(); // récupère l'utilisateur connecté
    let isAuth = auth.user !== null; // si pas de données utilisateur alors c'est que l'on est déconnecté
    //Disable ou non le bouton de la validation
    const [validate, setValidate] = useState(false);
    // Navigation
    const navigate = useNavigate();
    //Déclaration du loader pour l'affichage des Skeleton
    const [loading, setLoading] = useState<boolean>(true);
    const [hasContractCurrentUser, setHasContractCurrentUser] = useState<boolean>(false);
    //Création de données par défaut pour la page
    const [pageContent, setPageContent] = useState<PageContentDto>();
    //Récupéartion des données pour la popup
    const [dialogContent, setDialogContent] = useState<PageContentDto>();
    // Key pour savoir si la popup a déjà été visualisée
    let [key, setKey] = useLocalStorage('key', '');
    let [keyWithAuth, setKeyWithAuth] = useLocalStorage('keywithauth', '');

    //Open où non la popup d'info
    const [openPopup, setOpen] = React.useState(false);
    //Récupération de la valeur du cookie
    let acceptCookie = Cookies.get('acceptGoogle');
    //Use confirm dialog
    const confirm = useConfirm();

    const handleOpenDialog = () => {
        setOpen(true);
    };
    const handleCloseDialog = () => {
        setOpen(false);
    };

    // Comportement au submit du formulaire Login
    const handlerConnectionSubmit = async (e: AuthDto) => {
        setValidate(true);
        await auth.signin(e);
        localStorage.removeItem('login');
        setValidate(false);
        // Pour rafraichir la page quand on se connecte depuis la page home
        navigate('/', { replace: true });
    };

    // Récupère les données du back permettant de construire la page
    useEffect(() => {
        if (dataFetchedRef.current) {
            return;
        }
        dataFetchedRef.current = true;

        getPageContent('home')
            .then(async result => {
                //On met le résultat de la requete dans la variable envoyé à la page
                if (result) {
                    setPageContent(await toPageContentDTO(result));
                }
                setLoading(false);
            })
            .catch(error => {
                //Affichage du message d'erreur si la requete ne récupère rien
                console.error('Erreur lors de la récupération du contenu de la page');
                showAxiosMessageError(error, showMessage);
            });

        if (isAuth) {
            getPageContent('popupinfo_connected')
                .then(async result => {
                    if (result && result.length > 0) {
                        const content = await toPageContentDTO(result);
                        if (
                            content?.content &&
                            content?.content.trim() !== '' &&
                            content?.content.trim() !== '<p><br></p>'
                        ) {
                            if (content?.key !== keyWithAuth || content?.permanent) {
                                setDialogContent(content);
                                handleOpenDialog();
                                setKeyWithAuth(content?.key);
                            }
                        }
                    }
                })
                .catch(error => {
                    console.error('Erreur lors de la récupération du contenu de la popup');
                    showAxiosMessageError(error, showMessage);
                });
        } else {
            getPageContent('popupinfo_notconnected')
                .then(async result => {
                    //On met le résultat de la requete dans la variable envoyé à la page
                    if (result && result.length > 0) {
                        const content = await toPageContentDTO(result);
                        if (
                            content?.content &&
                            content?.content.trim() !== '' &&
                            content?.content.trim() !== '<p><br></p>'
                        ) {
                            if (content?.key !== key || content?.permanent) {
                                setDialogContent(content);
                                handleOpenDialog();
                                setKey(content?.key);
                            }
                        }
                    }
                })
                .catch(error => {
                    //Affichage du message d'erreur si la requete ne récupère rien
                    console.error('Erreur lors de la récupération du contenu de la popup');
                    showAxiosMessageError(error, showMessage);
                });
        }

        if (acceptCookie === 'false') {
            getPageContent('reactivatecookies')
                .then(result => {
                    const content = result?.find(entry => entry.codeChamps === 'content')?.valeur;
                    const title = result?.find(entry => entry.codeChamps === 'title')?.valeur;
                    confirm({
                        title: title,
                        description: parse(content as string),
                        cancellationText: 'Annuler',
                    })
                        .then(() => {
                            Cookies.set('acceptGoogle', 'true');
                        })
                        .catch(() => console.warn('Cookie non accepté.'));
                })
                .catch(() => {
                    console.error('Erreur lors de la récupération du texte de la réactivation des cookies.');
                });
        }
        //https://stackoverflow.com/questions/55840294/how-to-fix-missing-dependency-warning-when-using-useeffect-react-hook
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isAuth) {
            hasContract()
                .then(result => {
                    setHasContractCurrentUser(result);
                })
                .catch(error => {
                    //Affichage du message d'erreur si la requete ne récupère rien
                    console.error('Erreur lors de la récupération des contrats de la personne connectée', error);
                    showAxiosMessageError(error, showMessage);
                });
        }
    }, [isAuth, showMessage]);

    return (
        <Fragment>
            <HomeUi
                params={pageContent}
                handlerConnectionSubmit={handlerConnectionSubmit}
                validate={validate}
                isAuth={isAuth}
                loading={loading}
                hasContract={hasContractCurrentUser}
            />
            <DialogCustom params={dialogContent} open={openPopup} handleCloseDialog={handleCloseDialog} />
        </Fragment>
    );
}
